import * as SliderPrimitive from '@radix-ui/react-slider';
import { cn } from '@/lib/utils';
import { formatRadius } from '@/lib/format';

export interface RadiusSliderProps {
  value: number;
  onChange: (next: number) => void;
  min?: number;
  max?: number;
  step?: number;
  className?: string;
}

/**
 * `<RadiusSlider>` — single global radius (mi) for both list inclusion
 * and per-card amenity badge counts.
 *
 * UI-SPEC § 3.8:
 * - 0.1 – 3.0 mi, step 0.1, default 0.5 mi.
 * - Wraps Radix `<SliderPrimitive.Root>` directly (instead of the shadcn
 *   `<Slider>` wrapper) so we can paint the track / range / thumb in
 *   Phase-05 colours without the shadcn `bg-secondary` etc. overrides.
 * - Track: `h-1 bg-ocean-800`. Range: `bg-tide-400`. Thumb:
 *   `bg-foam-50 border-2 border-tide-400`. `:active`/data-state=dragging
 *   thumb → `bg-bronze-500 scale-[1.15]` (one of the 6 places bronze
 *   appears, § 1.1.1 #3).
 * - Snap ticks (decorative only — Radix never snaps): faint 1px lines at
 *   0.25 / 0.5 / 1.0 / 2.0 mi positions on the track.
 *
 * The slider does NOT debounce. `onChange` fires on every Radix `value`
 * change. Plan 05-05's `useFilters` hook does the 300ms URL-write debounce.
 *
 * ARIA: Radix sets `role=slider` etc. for us; we add `aria-label="Filter
 * radius in miles"` and an explicit `aria-valuetext` for screen-reader
 * announcement of the human-readable value.
 */
export function RadiusSlider({
  value,
  onChange,
  min = 0.1,
  max = 3.0,
  step = 0.1,
  className,
}: RadiusSliderProps) {
  const tickPositions = [0.25, 0.5, 1.0, 2.0];

  return (
    <div
      className={cn(
        'flex items-center gap-3 min-w-[280px]',
        className,
      )}
    >
      <span className="text-sm font-semibold text-foam-50/80 select-none">
        Radius
      </span>
      <SliderPrimitive.Root
        value={[value]}
        min={min}
        max={max}
        step={step}
        onValueChange={(values) => {
          const next = values[0];
          if (next != null) onChange(next);
        }}
        // Plan 05-06 a11y fix: aria-label + aria-valuetext used to live on
        // SliderPrimitive.Root, but Radix renders Root as a plain <span>
        // (no role). axe's aria-allowed-attr rule flags aria-valuetext on a
        // role-less element — and aria-input-field-name flags the inner
        // [role=slider] (the Thumb) for having no accessible name. Both are
        // resolved by moving aria-label + aria-valuetext to the Thumb.
        className="relative flex w-full grow touch-none select-none items-center h-5"
      >
        <SliderPrimitive.Track className="relative h-1 w-full grow overflow-hidden rounded-full bg-ocean-800">
          <SliderPrimitive.Range className="absolute h-full bg-tide-400" />
          {/* Decorative tick marks (not snapping) */}
          {tickPositions.map((mi) => {
            const pct = ((mi - min) / (max - min)) * 100;
            return (
              <span
                key={mi}
                aria-hidden
                className="absolute top-0 h-full w-px bg-foam-50/30"
                style={{ left: `${pct}%` }}
              />
            );
          })}
        </SliderPrimitive.Track>
        <SliderPrimitive.Thumb
          aria-label="Filter radius in miles"
          aria-valuetext={`${value.toFixed(1)} miles`}
          className={cn(
            'block w-5 h-5 rounded-full bg-foam-50 border-2 border-tide-400 shadow-md',
            'transition-transform duration-100',
            'data-[state=active]:bg-bronze-500 data-[state=active]:scale-[1.15]',
            'focus-visible:outline-2 focus-visible:outline-bronze-500/60 focus-visible:outline-offset-2',
            'active:bg-bronze-500 active:scale-[1.15]',
          )}
        />
      </SliderPrimitive.Root>
      <span className="text-sm font-semibold text-foam-50 tabular-nums w-[64px] text-right">
        {formatRadius(value)}
      </span>
    </div>
  );
}
